import React, { useState } from 'react'
import { COLORS, FONT, MOTION, RADIUS, SHADOW } from './tokens'
import { ArrowIcon, CheckCircleIcon, Spinner } from './icons'

interface ActionCardProps {
    primary?: boolean
    badge?: string
    /**
     * When set, renders a small accent "ribbon" straddling the top edge of the
     * card (e.g. "Recommended"). Used to flag the suggested option at a glance.
     * Hidden while pending/consumed so it never competes with status feedback.
     */
    ribbon?: string
    /**
     * Optional leading icon tile. When omitted (the default across both
     * breakpoints) the card renders as one clean, centered, icon-free line so
     * the desktop and mobile actions read as the same control.
     */
    icon?: React.ReactNode
    title: string
    description?: string
    disabled?: boolean
    pending?: boolean
    consumed?: boolean
    consumedLabel?: string
    /**
     * When provided, the card renders as an `<a href>` (rather than a
     * `<button>`) so a tap navigates natively — required for iOS Universal
     * Links / Android App Links, which do NOT fire from a JS `window.location`
     * assignment. `onClick` still fires (for logging / state) and the
     * navigation proceeds. While the card is inactive (pending/consumed/
     * disabled) it reverts to a non-navigating `<button>` so a locked card
     * can't fire the link. Referrer is suppressed so the token in the URL
     * isn't leaked cross-origin.
     */
    href?: string
    onClick: () => void
}

export const ActionCard: React.FC<ActionCardProps> = ({
    primary = false,
    badge,
    ribbon,
    icon,
    title,
    description,
    disabled,
    pending,
    consumed,
    consumedLabel,
    href,
    onClick,
}) => {
    const [hovered, setHovered] = useState(false)
    const [pressed, setPressed] = useState(false)
    // Read once on mount. Only lift/darken on devices that can actually hover
    // (desktop pointers + keyboard focus): a touch tap focuses the button with
    // no matching blur, so without this gate the card would stick in its
    // lifted/darkened state after the finger lifts. `reduceMotion` honours the
    // OS "reduce motion" setting the same way App.css gates its animations.
    const [canHover] = useState(
        () =>
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(hover: hover)').matches,
    )
    const [reduceMotion] = useState(
        () =>
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    )
    // Pending and consumed both use aria-disabled rather than the native
    // `disabled` attribute so keyboard focus survives the transitions
    // (WCAG 2.4.3). The click guard below keeps the card inert. Explicit
    // `disabled` from the caller still maps to the native attribute.
    const inactive = disabled || pending || consumed
    const active = hovered && !inactive && canHover
    const hasIcon = icon != null

    const fg = primary ? COLORS.ivory : COLORS.action
    const bg = primary ? COLORS.action : COLORS.ivory
    const sub = primary ? 'rgba(255,255,255,0.82)' : COLORS.neutral500
    const border = primary ? COLORS.action : COLORS.neutral200
    const hoverBorder = primary ? COLORS.action : COLORS.neutral300
    const iconBg = primary ? 'rgba(255,255,255,0.14)' : COLORS.neutral100
    const restingShadow = primary ? SHADOW.md : 'none'
    const hoverShadow = primary ? SHADOW.lg : SHADOW.sm

    const handleClick = () => {
        if (inactive) return
        onClick()
    }

    const renderedBadge = consumed && consumedLabel ? consumedLabel : badge
    const rightGlyph = consumed ? (
        <CheckCircleIcon size={18} />
    ) : pending ? (
        <Spinner size={18} />
    ) : (
        <ArrowIcon size={18} />
    )

    const titleStyle: React.CSSProperties = {
        fontSize: 19,
        color: fg,
        letterSpacing: '-0.01em',
        lineHeight: 1.2,
    }

    const badgeChip = renderedBadge ? (
        <span
            style={{
                fontSize: 10,
                letterSpacing: '0.07em',
                background: consumed ? COLORS.neutral200 : COLORS.orange,
                color: consumed ? COLORS.neutral700 : COLORS.noir,
                padding: '3px 9px',
                borderRadius: RADIUS.pill,
                textTransform: 'uppercase',
                lineHeight: 1.4,
            }}
        >
            {renderedBadge}
        </span>
    ) : null

    const glyph = (
        <span
            style={{
                color: fg,
                opacity: 0.65,
                display: 'inline-flex',
                alignItems: 'center',
                flexShrink: 0,
            }}
        >
            {rightGlyph}
        </span>
    )

    // Accent "Recommended" ribbon straddling the top edge — teal on viridian is
    // the brand's primary accent and reads at a glance. Hidden once the card is
    // working/used so it never sits alongside the spinner / "already used" badge.
    const showRibbon = ribbon && !pending && !consumed
    const recommendedRibbon = showRibbon ? (
        <span
            style={{
                position: 'absolute',
                top: -11,
                left: '50%',
                transform: 'translateX(-50%)',
                background: COLORS.teal,
                color: COLORS.action,
                fontSize: 10,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                padding: '4px 12px',
                borderRadius: RADIUS.pill,
                lineHeight: 1.4,
                boxShadow: SHADOW.sm,
                whiteSpace: 'nowrap',
            }}
        >
            {ribbon}
        </span>
    ) : null

    const cardStyle: React.CSSProperties = {
        position: 'relative',
        width: '100%',
        // The recommended card is a touch taller so it reads as the hero
        // and gives the straddling ribbon room to breathe. Keyed off the
        // `ribbon` prop (not showRibbon) so the height stays fixed when the
        // ribbon hides on tap — otherwise the card would shrink mid-press.
        padding: ribbon ? '23px 20px 21px' : '18px 20px',
        borderRadius: RADIUS.md,
        border: `1.5px solid ${active ? hoverBorder : border}`,
        background: bg,
        color: fg,
        fontFamily: FONT.display,
        // Anchor variant would otherwise underline its text.
        textDecoration: 'none',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: hasIcon ? 'nowrap' : 'wrap',
        gap: hasIcon ? 15 : 9,
        alignItems: 'center',
        justifyContent: hasIcon ? 'flex-start' : 'center',
        textAlign: hasIcon ? 'left' : 'center',
        cursor: inactive ? 'default' : 'pointer',
        transition: reduceMotion
            ? 'none'
            : `transform ${MOTION.ease}, box-shadow ${MOTION.ease}, border-color ${MOTION.ease}, filter ${MOTION.ease}, opacity ${MOTION.ease}`,
        transform: reduceMotion
            ? 'none'
            : pressed
              ? 'scale(0.985)'
              : active
                ? 'translateY(-1px)'
                : 'translateY(0)',
        boxShadow: active ? hoverShadow : restingShadow,
        filter: active && primary ? 'brightness(0.95)' : 'none',
        opacity: consumed ? 0.6 : disabled ? 0.85 : 1,
        // `revert` restores the browser default focus ring (WCAG 2.4.7).
        outline: 'revert',
    }

    // Shared interaction + presentation props. Every key is valid on both
    // <a> and <button>, so the two branches below stay in sync.
    const sharedProps = {
        onClick: handleClick,
        onMouseEnter: () => setHovered(true),
        onMouseLeave: () => {
            setHovered(false)
            setPressed(false)
        },
        onFocus: () => setHovered(true),
        onBlur: () => {
            setHovered(false)
            setPressed(false)
        },
        onMouseDown: () => !inactive && setPressed(true),
        onMouseUp: () => setPressed(false),
        onTouchStart: () => !inactive && setPressed(true),
        onTouchEnd: () => setPressed(false),
        onTouchCancel: () => setPressed(false),
        style: cardStyle,
    }

    const content = (
        <>
            {recommendedRibbon}
            {hasIcon ? (
                <>
                    <div
                        style={{
                            width: 46,
                            height: 46,
                            borderRadius: RADIUS.pill,
                            background: iconBg,
                            color: fg,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        {icon}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                flexWrap: 'wrap',
                            }}
                        >
                            <span style={titleStyle}>{title}</span>
                            {badgeChip}
                        </div>
                        {description && (
                            <p
                                style={{
                                    fontSize: 13,
                                    color: sub,
                                    marginTop: 4,
                                    marginBottom: 0,
                                    lineHeight: 1.45,
                                }}
                            >
                                {description}
                            </p>
                        )}
                    </div>
                    {glyph}
                </>
            ) : (
                <>
                    <span style={titleStyle}>{title}</span>
                    {badgeChip}
                    {glyph}
                    {description && (
                        <p
                            style={{
                                width: '100%',
                                margin: 0,
                                fontSize: 13,
                                color: sub,
                                lineHeight: 1.45,
                                textAlign: 'center',
                            }}
                        >
                            {description}
                        </p>
                    )}
                </>
            )}
        </>
    )

    // Render as a real link only when there's an href AND the card is live.
    // Universal / App Links require a genuine anchor navigation. A locked card
    // (pending/consumed/disabled) reverts to a <button> so it can't navigate
    // and keeps keyboard focus during the transient locked state.
    if (href && !inactive) {
        return (
            <a
                {...sharedProps}
                href={href}
                referrerPolicy="no-referrer"
                aria-busy={pending || undefined}
            >
                {content}
            </a>
        )
    }

    return (
        <button
            {...sharedProps}
            type="button"
            disabled={disabled}
            aria-disabled={pending || consumed || undefined}
            aria-busy={pending || undefined}
        >
            {content}
        </button>
    )
}
